import { useState, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Eye, X as XIcon } from 'lucide-react';
import axios from 'axios';
import TicketDrawer from '../components/TicketDrawer';
import { StatusBadge } from '../components/VisualAtoms';
import ExhibitionPublicUrlStrip from '../components/ExhibitionPublicUrlStrip';
import { humanize, toneForTicketStatus } from '../theme/adminVisualSystem';
import SectionAnalyticsHeader from '../components/SectionAnalyticsHeader';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUSES = ['all', 'pending', 'accepted', 'declined', 'waitlist', 'deleted'];

function Badge({ status }) {
  return <StatusBadge status={status || 'pending'} kind="ticket" />;
}

// ─── Single-row action button (matches the dark/cyan admin style) ───
function RowActionButton({ tone, onClick, label, dataTestId }) {
  const tones = {
    accept: 'border-[rgba(126,212,160,0.4)] text-[#7ed4a0] hover:bg-[rgba(126,212,160,0.08)] hover:shadow-[0_0_10px_rgba(126,212,160,0.3)]',
    decline: 'border-[rgba(232,139,160,0.4)] text-[#e88ba0] hover:bg-[rgba(232,139,160,0.07)] hover:shadow-[0_0_10px_rgba(232,139,160,0.25)]',
    waitlist: 'border-[rgba(228,192,122,0.45)] text-[#e4c07a] hover:bg-[rgba(228,192,122,0.08)] hover:shadow-[0_0_10px_rgba(228,192,122,0.3)]',
    reset: 'border-[#333] text-[#aaa] hover:bg-[#1a1a1a]',
    delete: 'border-[rgba(232,139,160,0.45)] text-[#e88ba0] hover:bg-[rgba(232,139,160,0.07)] hover:shadow-[0_0_10px_rgba(232,139,160,0.25)]',
    restore: 'border-[rgba(126,212,160,0.45)] text-[#7ed4a0] hover:bg-[rgba(126,212,160,0.08)] hover:shadow-[0_0_10px_rgba(126,212,160,0.25)]',
    remove: 'border-[rgba(232,99,99,0.55)] text-[#e86363] hover:bg-[rgba(232,99,99,0.09)] hover:shadow-[0_0_10px_rgba(232,99,99,0.3)]',
  };
  return (
    <button
      onClick={onClick}
      data-clickable="true"
      data-testid={dataTestId}
      className={`px-2 py-1 text-[9px] font-mono uppercase tracking-[0.14em] border transition-all ${tones[tone] || tones.reset}`}
    >
      {label}
    </button>
  );
}

export default function TicketsPage() {
  const { exhibitionId, selectedExhibition } = useOutletContext();
  const [tickets, setTickets] = useState([]); // active (non-deleted) tickets
  const [deletedTickets, setDeletedTickets] = useState([]); // soft-deleted tickets
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [active, setActive] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const showingDeletedTab = filter === 'deleted';

  const fetchTickets = async () => {
    if (!exhibitionId) return;
    setLoading(true);
    try {
      // Fetch active + deleted in parallel so the analytics header has true totals.
      const [activeRes, delRes] = await Promise.all([
        axios.get(`${API}/admin/tickets?exhibition_id=${exhibitionId}`),
        axios.get(`${API}/admin/tickets?exhibition_id=${exhibitionId}&deleted=true`),
      ]);
      setTickets(activeRes.data);
      setDeletedTickets(delRes.data);
    } catch {
      /* silent */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [exhibitionId]);

  // Reset selection whenever the tab/filter changes — the selectable set has
  // changed so we never want stale ids carrying over.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [filter]);

  const updateTicket = async (id, decision_status, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await axios.patch(`${API}/admin/tickets/${id}`, { decision_status });
      toast.success(`Ticket ${decision_status}`);
      setTickets((list) => list.map((t) => (t.id === id ? res.data : t)));
      if (active && active.id === id) setActive(res.data);
    } catch {
      toast.error('Update failed');
    }
  };

  const softDeleteOne = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await axios.patch(`${API}/admin/tickets/${id}/delete`);
      // Remove from active list, add to deleted list.
      setTickets((list) => list.filter((t) => t.id !== id));
      setDeletedTickets((list) => [res.data, ...list]);
      if (active && active.id === id) setActive(null);
      toast.success('Ticket deleted');
    } catch {
      toast.error('Delete failed');
    }
  };

  const restoreOne = async (id, e) => {
    if (e) e.stopPropagation();
    try {
      const res = await axios.patch(`${API}/admin/tickets/${id}/restore`);
      setDeletedTickets((list) => list.filter((t) => t.id !== id));
      setTickets((list) => [res.data, ...list]);
      toast.success('Ticket restored');
    } catch {
      toast.error('Restore failed');
    }
  };

  const permanentDeleteOne = async (id, e) => {
    if (e) e.stopPropagation();
    if (!window.confirm('Remove this ticket request permanently? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/admin/tickets/${id}/permanent`);
      setDeletedTickets((list) => list.filter((t) => t.id !== id));
      setSelectedIds((s) => {
        const next = new Set(s);
        next.delete(id);
        return next;
      });
      toast.success('Ticket removed');
    } catch {
      toast.error('Remove failed');
    }
  };

  const handleDrawerUpdate = (updated) => {
    setTickets((list) => list.map((t) => (t.id === updated.id ? updated : t)));
  };

  // ─── Filtering ───────────────────────────────────────────────────
  const sourceList = showingDeletedTab ? deletedTickets : tickets;

  const filtered = useMemo(() => {
    let arr = sourceList;
    if (!showingDeletedTab && filter !== 'all') {
      arr = arr.filter((t) => (t.decision_status || 'pending') === filter);
    }
    if (search) {
      const s = search.toLowerCase();
      arr = arr.filter(
        (t) =>
          (t.email || '').toLowerCase().includes(s) ||
          (t.name || '').toLowerCase().includes(s),
      );
    }
    return arr;
  }, [sourceList, filter, search, showingDeletedTab]);

  // ─── Bulk selection helpers ──────────────────────────────────────
  const visibleIds = useMemo(() => filtered.map((t) => t.id), [filtered]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id));

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        visibleIds.forEach((id) => next.delete(id));
      } else {
        visibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const toggleOne = (id, e) => {
    if (e) e.stopPropagation();
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const runBulk = async (action) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (action === 'permanent_delete') {
      if (!window.confirm('Remove selected ticket requests permanently? This cannot be undone.')) return;
    }
    try {
      const { data } = await axios.post(`${API}/admin/tickets/bulk-action`, {
        ticket_ids: ids,
        action,
      });
      toast.success(`${humanize(action)} · ${data.updated_count || ids.length} updated${data.failed_count ? ` · ${data.failed_count} failed` : ''}`);
      await fetchTickets();
      clearSelection();
    } catch (err) {
      toast.error(err.response?.data?.detail || `Bulk ${action} failed`);
    }
  };

  // ─── Totals for the analytics header (exclude soft-deleted from per-status totals) ─
  const totals = useMemo(() => {
    const t = { pending: 0, accepted: 0, declined: 0, waitlist: 0, deleted: deletedTickets.length };
    for (const r of tickets) {
      const s = r.decision_status || 'pending';
      if (t[s] !== undefined) t[s] += 1;
    }
    return t;
  }, [tickets, deletedTickets]);

  return (
    <div className="space-y-6" data-testid="tickets-page">
      {selectedExhibition?.slug && (
        <ExhibitionPublicUrlStrip
          slug={selectedExhibition.slug}
          hash="tickets"
          label="Public ticket form"
          disabled={!selectedExhibition.ticket_requests_enabled}
          disabledReason="Tickets are disabled for this exhibition. Enable them on the Exhibitions tab to share a public form."
        />
      )}
      <SectionAnalyticsHeader
        title="Ticket Requests"
        helper={`${tickets.length} active · ${deletedTickets.length} deleted · click any row to review`}
        items={tickets}
        chartLabel="Daily Ticket Inflow"
        days={14}
        activeStatKey={filter}
        onStatClick={setFilter}
        stats={[
          { key: 'pending', label: 'Pending', value: totals.pending, tone: toneForTicketStatus('pending') },
          { key: 'accepted', label: 'Accepted', value: totals.accepted, tone: toneForTicketStatus('accepted') },
          { key: 'declined', label: 'Declined', value: totals.declined, tone: toneForTicketStatus('declined') },
          { key: 'waitlist', label: 'Waitlist', value: totals.waitlist, tone: toneForTicketStatus('waitlist') },
        ]}
      />

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setFilter(s)}
            data-clickable="true"
            data-testid={`tickets-filter-${s}`}
            className={`text-[10px] font-mono px-3 py-1.5 border rounded-none uppercase tracking-[0.14em] transition-all ${
              filter === s
                ? (s === 'deleted'
                    ? 'border-[#e88ba0] text-[#e88ba0] bg-[rgba(232,139,160,0.06)] shadow-[0_0_10px_rgba(232,139,160,0.18)]'
                    : 'border-[#90E0EF] text-[#90E0EF] bg-[#90E0EF]/5 shadow-[0_0_10px_rgba(144,224,239,0.18)]')
                : 'border-[#222] text-[#777] hover:text-white hover:border-[#333]'
            }`}
          >
            {s === 'all' ? 'All' : humanize(s)}
            {s === 'deleted' && deletedTickets.length > 0 && (
              <span className="ml-1.5 text-[9px] opacity-70">({deletedTickets.length})</span>
            )}
          </button>
        ))}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name or email..."
          data-testid="tickets-search"
          className="ml-auto bg-transparent border border-[#222] text-white text-xs font-mono px-3 py-1.5 w-48 focus:outline-none focus:border-[#90E0EF]"
        />
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          data-testid="tickets-bulk-bar"
          className="flex flex-wrap items-center gap-2 border border-[#90E0EF]/30 bg-[rgba(144,224,239,0.04)] px-3 py-2"
        >
          <span className="text-[10px] font-mono uppercase tracking-[0.14em] text-[#90E0EF]">
            {selectedIds.size} selected
          </span>
          <button
            onClick={clearSelection}
            data-testid="tickets-bulk-clear"
            className="text-[10px] font-mono text-[#777] hover:text-white inline-flex items-center gap-1 ml-1"
          >
            <XIcon className="w-3 h-3" /> clear
          </button>
          <div className="ml-auto flex flex-wrap items-center gap-1.5">
            {showingDeletedTab ? (
              <>
                <RowActionButton tone="restore" label="Restore" dataTestId="tickets-bulk-restore" onClick={() => runBulk('restore')} />
                <RowActionButton tone="remove" label="Remove" dataTestId="tickets-bulk-remove" onClick={() => runBulk('permanent_delete')} />
              </>
            ) : (
              <>
                <RowActionButton tone="accept" label="Accept" dataTestId="tickets-bulk-accept" onClick={() => runBulk('accept')} />
                <RowActionButton tone="decline" label="Decline" dataTestId="tickets-bulk-decline" onClick={() => runBulk('decline')} />
                <RowActionButton tone="waitlist" label="Waitlist" dataTestId="tickets-bulk-waitlist" onClick={() => runBulk('waitlist')} />
                <RowActionButton tone="reset" label="Reset" dataTestId="tickets-bulk-reset" onClick={() => runBulk('reset')} />
                <RowActionButton tone="delete" label="Delete" dataTestId="tickets-bulk-delete" onClick={() => runBulk('delete')} />
              </>
            )}
          </div>
        </motion.div>
      )}

      {/* Table */}
      <div className="border border-[#1a1a1a] overflow-x-auto">
        <table className="w-full text-xs font-mono">
          <thead>
            <tr className="border-b border-[#1a1a1a] text-[#666] uppercase tracking-[0.15em]">
              <th className="p-3 w-8">
                <input
                  type="checkbox"
                  checked={allVisibleSelected}
                  ref={(el) => { if (el) el.indeterminate = !allVisibleSelected && someVisibleSelected; }}
                  onChange={toggleAllVisible}
                  data-testid="tickets-select-all"
                  className="accent-[#90E0EF] cursor-pointer"
                  aria-label="Select all visible"
                />
              </th>
              <th className="text-left p-3">Name</th>
              <th className="text-left p-3">Email</th>
              <th className="text-left p-3">Profession</th>
              <th className="text-left p-3">Status</th>
              <th className="text-left p-3">Date</th>
              <th className="text-left p-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-[#444]">
                  Loading...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="p-8 text-center text-[#444]">
                  {showingDeletedTab ? 'No deleted tickets' : 'No tickets found'}
                </td>
              </tr>
            ) : (
              filtered.map((t, i) => (
                <motion.tr
                  key={t.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: i * 0.02 }}
                  onClick={() => setActive(t)}
                  data-clickable="true"
                  data-testid={`ticket-row-${t.id}`}
                  className={`group border-b border-[#111] hover:bg-[#111] transition-colors cursor-pointer ${
                    selectedIds.has(t.id) ? 'bg-[rgba(144,224,239,0.04)] border-l-2 border-l-[#90E0EF]' : 'hover:border-[#90E0EF]/15'
                  }`}
                >
                  <td className="p-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selectedIds.has(t.id)}
                      onChange={(e) => toggleOne(t.id, e)}
                      data-testid={`ticket-select-${t.id}`}
                      className="accent-[#90E0EF] cursor-pointer"
                      aria-label={`Select ${t.email}`}
                    />
                  </td>
                  <td className="p-3 text-[#f6f6f6] group-hover:text-white">
                    {t.name || '—'}
                  </td>
                  <td className="p-3 text-[#999] group-hover:text-[#ccc]">
                    <div className="flex items-center gap-1.5">
                      <span>{t.email}</span>
                      {t.source === 'invite' && (
                        <span
                          className="inline-flex items-center text-[9px] font-mono px-1.5 py-0.5 border uppercase tracking-[0.18em]"
                          style={{ color: '#c89dff', borderColor: 'rgba(200,157,255,0.45)', background: 'rgba(200,157,255,0.08)' }}
                          data-testid={`ticket-invite-tag-${t.id}`}
                        >
                          Invite
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="p-3 text-[#888]">{t.profession || '—'}</td>
                  <td className="p-3">
                    <Badge status={t.decision_status || 'pending'} />
                  </td>
                  <td className="p-3 text-[#555]">
                    {t.created_at ? new Date(t.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-1">
                      {showingDeletedTab ? (
                        <>
                          <RowActionButton tone="restore" label="Restore" dataTestId={`ticket-restore-${t.id}`} onClick={(e) => restoreOne(t.id, e)} />
                          <RowActionButton tone="remove" label="Remove" dataTestId={`ticket-remove-${t.id}`} onClick={(e) => permanentDeleteOne(t.id, e)} />
                        </>
                      ) : (
                        <>
                          {t.decision_status !== 'accepted' && (
                            <RowActionButton tone="accept" label="Accept" onClick={(e) => updateTicket(t.id, 'accepted', e)} />
                          )}
                          {t.decision_status !== 'declined' && (
                            <RowActionButton tone="decline" label="Decline" onClick={(e) => updateTicket(t.id, 'declined', e)} />
                          )}
                          {t.decision_status !== 'waitlist' && (
                            <RowActionButton tone="waitlist" label="Waitlist" onClick={(e) => updateTicket(t.id, 'waitlist', e)} />
                          )}
                          {t.decision_status && t.decision_status !== 'pending' && (
                            <RowActionButton tone="reset" label="Reset" onClick={(e) => updateTicket(t.id, 'pending', e)} />
                          )}
                          <RowActionButton tone="delete" label="Delete" dataTestId={`ticket-delete-${t.id}`} onClick={(e) => softDeleteOne(t.id, e)} />
                        </>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setActive(t);
                        }}
                        data-clickable="true"
                        data-testid={`ticket-view-${t.id}`}
                        className="ml-1 w-6 h-6 flex items-center justify-center text-[#666] hover:text-[#90E0EF] transition-colors opacity-0 group-hover:opacity-100"
                        aria-label="View details"
                      >
                        <Eye className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Drawer */}
      <TicketDrawer
        ticket={active}
        onClose={() => setActive(null)}
        onUpdate={handleDrawerUpdate}
      />
    </div>
  );
}
